import {
    combineReducers,
    configureStore,
    createListenerMiddleware,
} from "@reduxjs/toolkit";
import {TypedUseSelectorHook, useDispatch, useSelector} from "react-redux";
import type {ThunkAction} from "redux-thunk";

import * as auth from "@client/features/auth";
import * as boot from "@client/features/boot";
import * as dialogs from "@client/features/dialogs";
import * as notifications from "@client/features/notifications";
import * as orm from "@client/features/orm";
import * as scenes from "@client/features/scenes";
import * as search from "@client/features/search";
import * as subscriptions from "@client/features/subscriptions";

import * as analytics from "@client/shared/analytics";

const rootReducer = combineReducers({
    auth: auth.reducer,
    boot: boot.reducer,
    dialogs: dialogs.reducer,
    notifications: notifications.reducer,
    orm: orm.reducer,
    scenes: scenes.reducer,
    search: search.reducer,
    subscriptions: subscriptions.reducer,
});

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
    predicate: () => true,
    effect: (_action) => {
        const action = _action as Action;
        switch (action.type) {
            case "EXPAND_MEAL":
            case "COLLAPSE_MEAL":
            case "SHOW_FEELER":
            case "SHOW_HELP":
            case "DISCONNECTED":
            case "ACKNOWLEDGE_FEELER":
            case "ANSWER_FEELER_POSITIVELY":
            case "ANSWER_FEELER_NEGATIVELY":
            case "AGREE_TO_RATE":
            case "DECLINE_TO_RATE":
            case "AGREE_TO_PROVIDE_FEEDBACK":
            case "DECLINE_TO_PROVIDE_FEEDBACK":
            case "CREATE_SUBSCRIPTION_SUCCESS":
            case "DELETE_SUBSCRIPTION_SUCCESS":
            case "NEEDS_SUBSCRIPTION":
            case "SUBSCRIBING":
            case "ERROR_SUBSCRIBING":
            case "SHOW_PAYPAL":
            case "START_SCANNING":
            case "NO_RESULTS":
            case "ERROR_SCANNING":
                void analytics.track(action.type);
                break;
            case "SHARE_SUCCESS":
                void analytics.track(action.type, {share_type: action.share.type});
                break;
            case "SEARCHED":
                void analytics.track(action.type, action);
                break;
            case "CREATE_JOURNAL_ENTRIES_SUCCESS":
                void analytics.track(action.type, {
                    count: action.count,
                    meal_mode: action.meal_mode,
                    journal_mode: action.journal_mode,
                });
                break;
            case "SEARCH_LOADED":
                if (action.from_cache === false) {
                    void analytics.track(action.type, {
                        request_time: action.request_time,
                    });
                }
        }
    },
});

export const makeStore = () =>
    configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware().prepend(listenerMiddleware.middleware),
    });

export const store = makeStore();

import.meta.hot?.on("vite:beforeUpdate", () => {
    store.dispatch({type: "SET_NEXT_ANIMATION", animation: "disabled"});
});

export type Action =
    | auth.Actions
    | boot.Actions
    | dialogs.Actions
    | notifications.Actions
    | orm.Actions
    | scenes.Actions
    | search.Actions
    | subscriptions.Actions;

export type RootState = ReturnType<typeof store.getState>;

export interface AppDispatch {
    /** Accepts a thunk function, runs it, and returns whatever the thunk itself returns */
    <ReturnType>(
        thunkAction: ThunkAction<ReturnType, RootState, never, Action>,
    ): ReturnType;
    /** Accepts a standard action object, and returns that action object */
    (action: Action): Action;
    // /** A union of the other two overloads for TS inference purposes */
    <ReturnType>(
        action: Action | ThunkAction<ReturnType, RootState, never, Action>,
    ): Action | ReturnType;
}

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export type GetState = () => RootState;
